import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, getDocs, doc, updateDoc, increment, getDoc
} from 'firebase/firestore';
import { Send, ThumbsUp, User, ExternalLink, Calendar, MapPin, History } from 'lucide-react';
import './JournalClub.css';

function JournalClub() {
  const [papers, setPapers] = useState([]);
  const [pastDiscussions, setPastDiscussions] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDesc, setExpandedDesc] = useState(null);
  const [form, setForm] = useState({
    title: '', author: '', name: '', link: '', description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const configSnap = await getDoc(doc(db, 'config', 'info'));
      if (configSnap.exists()) {
        setConfig(configSnap.data());
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
  }, []);

  const loadPapers = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'papers'));
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPapers(data.sort((a, b) => (b.votes || 0) - (a.votes || 0)));
    } catch (err) {
      console.error('Error loading papers:', err);
    }
  }, []);

  const loadPastDiscussions = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'past'));
      const data = snapshot.docs.map(d => d.data());
      setPastDiscussions(data);
    } catch (err) {
      console.error('Error loading past discussions:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadConfig(), loadPapers(), loadPastDiscussions()])
      .finally(() => setLoading(false));
  }, [loadConfig, loadPapers, loadPastDiscussions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'papers'), {
        title: form.title,
        author: form.author,
        name: form.name,
        link: form.link || null,
        description: form.description,
        votes: 0,
        discussionLeader: ''
      });
      setForm({ title: '', author: '', name: '', link: '', description: '' });
      await loadPapers();
    } catch (err) {
      console.error('Error submitting paper:', err);
    }
    setSubmitting(false);
  };

  const voteForPaper = async (id) => {
    const voted = JSON.parse(localStorage.getItem('votedPapers') || '[]');
    if (voted.includes(id)) {
      alert('You have already voted for this paper.');
      return;
    }
    try {
      await updateDoc(doc(db, 'papers', id), { votes: increment(1) });
      voted.push(id);
      localStorage.setItem('votedPapers', JSON.stringify(voted));
      await loadPapers();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const leadDiscussion = async (id) => {
    const name = prompt('Enter your name to lead the discussion:');
    if (!name) return;
    try {
      await updateDoc(doc(db, 'papers', id), { discussionLeader: name });
      await loadPapers();
    } catch (err) {
      console.error('Error updating leader:', err);
    }
  };

  const mostVoted = papers.length > 0
    ? papers.reduce((a, b) => (a.votes || 0) > (b.votes || 0) ? a : b)
    : null;

  const votedPapers = JSON.parse(localStorage.getItem('votedPapers') || '[]');

  return (
    <div className="journal-club-page">
      <div className="container">
        <section className="page-hero">
          <h1>Journal Club</h1>
          <p className="subtitle">
            Submit papers, vote for the next discussion topic, and track
            our reading history. Vote for the next research paper to be discussed!
          </p>
        </section>

        {loading ? (
          <div className="jc-loading">
            <div className="jc-spinner" />
            <p>Loading from Firestore...</p>
          </div>
        ) : (
          <>
            {/* Upcoming discussion */}
            {mostVoted && (
              <section className="jc-upcoming">
                <h2 className="section-title">Upcoming Discussion</h2>
                <div className="upcoming-card">
                  <div className="upcoming-meta">
                    <span className="upcoming-detail">
                      <Calendar size={15} />
                      {config?.date || 'TBA'}
                    </span>
                    <span className="upcoming-detail">
                      <MapPin size={15} />
                      {config?.loc || 'Room D, IF (PAN)'}
                    </span>
                  </div>
                  <h3>{mostVoted.title}</h3>
                  <p className="upcoming-author">by {mostVoted.author}</p>
                  <p className="upcoming-leader">
                    Discussion leader: <strong>{mostVoted.discussionLeader || 'To be announced'}</strong>
                  </p>
                </div>
              </section>
            )}

            {/* Submit paper */}
            <section className="section jc-submit">
              <h2 className="section-title">Submit a Paper</h2>
              <form className="jc-form" onSubmit={handleSubmit}>
                <div className="jc-form-grid">
                  <input
                    type="text"
                    placeholder="Paper Title *"
                    value={form.title}
                    onChange={e => setForm({...form, title: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Paper Author(s) *"
                    value={form.author}
                    onChange={e => setForm({...form, author: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Paper Link (optional)"
                    value={form.link}
                    onChange={e => setForm({...form, link: e.target.value})}
                  />
                </div>
                <textarea
                  placeholder="Short description *"
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  required
                  rows={3}
                />
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <Send size={16} />
                  {submitting ? 'Submitting...' : 'Submit Paper'}
                </button>
              </form>
            </section>

            {/* Vote for papers */}
            <section className="section jc-vote">
              <h2 className="section-title">
                Vote for Papers
                {config?.vote_date && (
                  <span className="vote-deadline">open till {config.vote_date}</span>
                )}
              </h2>
              
              {papers.length === 0 ? (
                <p className="text-muted">No papers submitted yet. Be the first!</p>
              ) : (
                <div className="papers-list">
                  {papers.map((paper) => (
                    <div className="paper-card" key={paper.id}>
                      <div className="paper-card-main">
                        <div className="paper-votes-count">
                          <span className="vote-number">{paper.votes || 0}</span>
                          <span className="vote-label">votes</span>
                        </div>
                        <div className="paper-card-info">
                          <h3>
                            {paper.link ? (
                              <a href={paper.link} target="_blank" rel="noopener noreferrer">
                                {paper.title} <ExternalLink size={13} />
                              </a>
                            ) : paper.title}
                          </h3>
                          <div className="paper-card-meta">
                            <span>by {paper.author}</span>
                            <span>Suggested by {paper.name}</span>
                            {paper.discussionLeader && (
                              <span>Leader: {paper.discussionLeader}</span>
                            )}
                          </div>
                          {paper.description && (
                            <button
                              className="desc-toggle"
                              onClick={() => setExpandedDesc(expandedDesc === paper.id ? null : paper.id)}
                            >
                              {expandedDesc === paper.id ? 'Hide description' : 'Show description'}
                            </button>
                          )}
                          {expandedDesc === paper.id && (
                            <p className="paper-desc">{paper.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="paper-card-actions">
                        <button
                          className={`vote-btn ${votedPapers.includes(paper.id) ? 'voted' : ''}`}
                          onClick={() => voteForPaper(paper.id)}
                          disabled={votedPapers.includes(paper.id)}
                        >
                          <ThumbsUp size={15} />
                          {votedPapers.includes(paper.id) ? 'Voted' : 'Vote'}
                        </button>
                        <button
                          className="lead-btn"
                          onClick={() => leadDiscussion(paper.id)}
                        >
                          <User size={15} /> Lead
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Past discussions */}
            {pastDiscussions.length > 0 && (
              <section className="section jc-past">
                <h2 className="section-title">
                  <History size={22} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Past Discussions
                </h2>
                <div className="past-table-wrap">
                  <table className="past-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastDiscussions.map((d, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            {d.link ? (
                              <a href={d.link} target="_blank" rel="noopener noreferrer">
                                {d.title}
                              </a>
                            ) : d.title}
                          </td>
                          <td>{d.date}</td>
                          <td>{d.lead}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default JournalClub;
